import { BaseResponseAPIModel, APIResponseModel } from '../models/BaseResponseModel.tsx';
import { BaseRequestAPIModel } from '../models/BaseRequestModel.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { fetchAPI } from '../api/GenericFetch.js';
import { BaseEntity } from '../models/BaseEntity';

const fetchDataTable = async (fetchRequest) => {
    const response = await fetchAPI(fetchRequest);   
    return response;
};

export function useReadMutation<T>({url = '', cacheKey = '', enableRefetch = false})
{
    cacheKey = cacheKey == '' ? url : cacheKey;
    const {
      data : { data = [] } = {},
      isError,
      isRefetching,
      isLoading,
      refetch,
    } = useQuery<APIResponseModel<T>>({
      queryKey: [
        cacheKey,
      ],
      queryFn: async () => {        
        const response = await fetchAPI({url, body: null});
        return response as APIResponseModel<T>;
      },
      enabled: enableRefetch,
    });

    return {
      data,
      isFetchReadLoading: isLoading,
      isFetchReadError: isError,
      isRefetchingRead: isRefetching,
      refetchDataTable: refetch,
    };
}

export function useCreateMutation<T extends BaseEntity>({cacheKey = ''}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: BaseRequestAPIModel<T>) => {
      let response = new BaseResponseAPIModel<T>(request.body, request.apiURLModel); 
      const fetchRequest =  {
          url : request.apiURLModel.url,
          method: 'POST', 
          body : request.body,
      };

      const fetchResponseData = await fetchAPI(fetchRequest);
      response = { ...response, ...fetchResponseData }; 
      return response;
    },
    onSuccess: async (response) => {     
        if (!response.isSuccess) return;
        queryClient.resetQueries({ queryKey: [cacheKey] });

        const fetchRequest = {
          url : response.requestModelUrl.additionalUrl,
        };
        let refetchDataTable = await fetchDataTable(fetchRequest);

        queryClient.setQueryData([cacheKey], (prevData) => ({            
            ...prevData,
            data: refetchDataTable.data  
        }));

        queryClient.invalidateQueries([cacheKey]);
    },
    //optimistic update
    // onMutate: async (body: T) => {        
    // },
     //onSettled: () => queryClient.invalidateQueries({ queryKey: [dataTableUserCacheKey] })
  });
}

//UPDATE hook
export function useUpdateMutation<T extends BaseEntity>({cacheKey = '', skipOptimisticUpdate = false}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: BaseRequestAPIModel<T>) => {
      let response = new BaseResponseAPIModel<T>(request.updatedRow, request.apiURLModel); 
      const fetchRequest = {
          url : request.apiURLModel.url,
          method: 'POST', 
          body : request.body
      };

      const fetchResponseData = await fetchAPI(fetchRequest);
      response = { ...response, ...fetchResponseData }; 
      return response;
    },
    onSuccess: async (response) => {
        if (!response.isSuccess || skipOptimisticUpdate) return;

        await queryClient.cancelQueries({queryKey : [cacheKey]});

        await queryClient.setQueryData([cacheKey], (prevData) => ({
          data: prevData.data.map((row) => (row.Id == response.body.Id ? response.body : row))
        }));

        //queryClient.invalidateQueries({ queryKey: [cacheKey] });
        queryClient.invalidateQueries([cacheKey]);
    },
     //optimistic update
    // onMutate: async (body: T) => {
    // },
    //onSettled: () => queryClient.invalidateQueries({ queryKey: [dataTableUserCacheKey] }),
  });
}

//DELETE hook
export function useDeleteMutation<T extends BaseEntity>({cacheKey = ''}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: BaseRequestAPIModel<T>) => {
      let response = new BaseResponseAPIModel<T>(request.body, request.apiURLModel); 
      const fetchRequest = {
          url : request.apiURLModel.url,
          method: 'POST', 
          body : request.body
      }
      const fetchResponseData = await fetchAPI(fetchRequest);
      response = { ...response, ...fetchResponseData };
      return response;
    },
    onSuccess: async (response) => {
        if (!response.isSuccess) return;

        await queryClient.cancelQueries({queryKey : [cacheKey]});
        const previousData = queryClient.getQueryData([cacheKey]);
        let updatedData = previousData.data.filter((row: T) => row.Id !== response.body.Id);
        //overwrite data
        previousData.data = updatedData;        
        queryClient.setQueryData([cacheKey], previousData);
    },
    //optimistic update
    // onMutate: async (selectedId: string) => {      
    // },
    //onSettled: () => queryClient.invalidateQueries({ queryKey: [dataTableUserCacheKey] })
  });
}
